import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import type {
  Pageable,
  CustomerFilterSpec,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CreateCustomerAddressRequest,
  CreateCustomerContactRequest,
  WrapperPageableResponseCustomerDTO,
  WrapperResponseCustomerDTO,
  WrapperResponseCustomerAddressDTO,
  WrapperResponseCustomerContactDTO,
  WrapperResponseReadFileResponse,
  WrapperResponseListReadFileResponse,
  AttachCustomerFileRequest,
  ReadFileResponse
} from '../core/models';

export type {
  CustomerDTO,
  CustomerAddressDTO,
  CustomerContactDTO,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CreateCustomerAddressRequest,
  CreateCustomerContactRequest,
  WrapperPageableResponseCustomerDTO,
  WrapperResponseCustomerDTO,
  WrapperResponseCustomerAddressDTO,
  WrapperResponseCustomerContactDTO,
  WrapperResponseReadFileResponse,
  WrapperResponseListReadFileResponse,
  AttachCustomerFileRequest,
  ReadFileResponse
} from '../core/models';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  getCustomers(
    spec: CustomerFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 } 
  ): Observable<WrapperPageableResponseCustomerDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null && item !== '') {
            params = params.append(key, String(item));
          }
        });
        return;
      }
      params = params.append(key, String(value));
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach(sort => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperPageableResponseCustomerDTO>(
      `${this.apiUrl}/customers`,
      { params }
    );
  }

  /**
   * Lista clientes (GET /customers/list) con filtros y paginación.
   */
  listCustomers(
    spec: CustomerFilterSpec = {},
    pageable: Pageable = { page: 0, size: 20 }
  ): Observable<WrapperPageableResponseCustomerDTO> {
    let params = new HttpParams();

    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });

    params = params.append('page', String(pageable.page));
    params = params.append('size', String(pageable.size));

    if (pageable.sort && pageable.sort.length > 0) {
      pageable.sort.forEach(sort => {
        params = params.append('sort', sort);
      });
    }

    return this.http.get<WrapperPageableResponseCustomerDTO>(
      `${this.apiUrl}/customers/list`,
      { params }
    );
  }

  createCustomer(request: CreateCustomerRequest): Observable<WrapperResponseCustomerDTO> {
    return this.http.post<WrapperResponseCustomerDTO>(`${this.apiUrl}/customers`, request);
  }

  getCustomerById(customerId: number): Observable<WrapperResponseCustomerDTO> {
    return this.http.get<WrapperResponseCustomerDTO>(`${this.apiUrl}/customers/${customerId}`);
  }

  updateCustomer(customerId: number, request: UpdateCustomerRequest): Observable<WrapperResponseCustomerDTO> {
    return this.http.put<WrapperResponseCustomerDTO>(`${this.apiUrl}/customers/${customerId}`, request);
  }

  deleteCustomer(customerId: number): Observable<WrapperResponseCustomerDTO> {
    return this.http.delete<WrapperResponseCustomerDTO>(`${this.apiUrl}/customers/${customerId}`);
  }

  createCustomerAddress(customerId: number, request: CreateCustomerAddressRequest): Observable<WrapperResponseCustomerAddressDTO> {
    return this.http.post<WrapperResponseCustomerAddressDTO>(`${this.apiUrl}/customers/${customerId}/addresses`, request);
  }

  updateCustomerAddress(
    customerId: number,
    addressId: number,
    request: CreateCustomerAddressRequest
  ): Observable<WrapperResponseCustomerAddressDTO> {
    return this.http.put<WrapperResponseCustomerAddressDTO>(
      `${this.apiUrl}/customers/${customerId}/addresses/${addressId}`,
      request
    );
  }

  createCustomerContact(customerId: number, request: CreateCustomerContactRequest): Observable<WrapperResponseCustomerContactDTO> {
    return this.http.post<WrapperResponseCustomerContactDTO>(`${this.apiUrl}/customers/${customerId}/contacts`, request);
  }

  updateCustomerContact(
    customerId: number,
    contactId: number,
    request: CreateCustomerContactRequest
  ): Observable<WrapperResponseCustomerContactDTO> {
    return this.http.put<WrapperResponseCustomerContactDTO>(
      `${this.apiUrl}/customers/${customerId}/contacts/${contactId}`,
      request
    );
  }

  deleteCustomerAddress(customerId: number, addressId: number): Observable<{ ok: boolean; code: number; message: string; body: null }> {
    return this.http.delete<{ ok: boolean; code: number; message: string; body: null }>(
      `${this.apiUrl}/customers/${customerId}/addresses/${addressId}`
    );
  }

  deleteCustomerContact(customerId: number, contactId: number): Observable<{ ok: boolean; code: number; message: string; body: null }> {
    return this.http.delete<{ ok: boolean; code: number; message: string; body: null }>(
      `${this.apiUrl}/customers/${customerId}/contacts/${contactId}`
    );
  }

  listCustomerAttachments(
    customerId: number,
    spec: { 'name[lk]'?: string } = {}
  ): Observable<WrapperResponseListReadFileResponse> {
    let params = new HttpParams();
    Object.entries(spec).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, String(value));
      }
    });
    return this.http.get<WrapperResponseListReadFileResponse>(
      `${this.apiUrl}/customers/${customerId}/attachments`,
      { params }
    );
  }

  attachCustomerFile(customerId: number, request: AttachCustomerFileRequest): Observable<WrapperResponseReadFileResponse> {
    return this.http.post<WrapperResponseReadFileResponse>(
      `${this.apiUrl}/customers/${customerId}/attach`,
      request
    );
  }

  deleteCustomerAttachment(customerId: number, fileId: number): Observable<{ ok: boolean; code: number; message: string; body: null }> {
    return this.http.delete<{ ok: boolean; code: number; message: string; body: null }>(
      `${this.apiUrl}/customers/${customerId}/attachments/${fileId}`
    );
  }
}
